import { Dispatch, SetStateAction, useEffect, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@components/ui/dropdown-menu";
import { Button } from "@components/ui/button";
import { ChevronDown } from "lucide-react";
import { Input } from "@components/ui/input";
import { Controller, useForm } from "react-hook-form";

type CustomDurationProps = {
  isCustomTimeSelectorOpen: boolean;
  setIsCustomTimeSelectorOpen: Dispatch<SetStateAction<boolean>>;
  onSave: ({
    duration,
    durationType,
  }: {
    duration: number;
    durationType: string;
  }) => void;
};

const durationTypes = ["hour", "minute"];
const MIN_HOUR = 1;
const MAX_HOUR = 24;

const MIN_MINUTE = 15;
const MAX_MINUTE = 1440;

const CustomDuration = (props: CustomDurationProps) => {
  const { control, watch, handleSubmit, setValue } = useForm({
    defaultValues: {
      duration: 0,
      durationType: durationTypes[0],
    },
    mode: "all",
  });
  const { isCustomTimeSelectorOpen, setIsCustomTimeSelectorOpen } = props;

  const selectedDurationType = watch("durationType");

  const handleSave = (formData) => {
    props?.onSave(formData);
    setIsCustomTimeSelectorOpen(false);
  };

  useEffect(() => {
    if (selectedDurationType === "minute") {
      setValue("duration", MIN_MINUTE);
    } else if (selectedDurationType === "hour") {
      setValue("duration", MIN_HOUR);
    }
  }, [selectedDurationType, setValue]);

  return (
    <Dialog
      open={isCustomTimeSelectorOpen}
      onOpenChange={setIsCustomTimeSelectorOpen}
    >
      <DialogContent className="bg-white w-[340px] max-w-[340px]">
        <DialogHeader>
          <DialogTitle>Custom Duration?</DialogTitle>
          <DialogDescription className="font-medium text-xs text-light-black">
            Set a custom meeting length that’s not listed in the presets.
          </DialogDescription>
        </DialogHeader>
        <form className="flex gap-4 my-1">
          <Controller
            name="duration"
            control={control}
            rules={{
              validate: (value) => {
                if (selectedDurationType === "minutes") {
                  if (value < MIN_MINUTE) return "Minimum is 30 minutes";
                  if (value > MAX_MINUTE) return "Maximum is 1440 minutes";
                } else if (selectedDurationType === "hours") {
                  if (value < MIN_HOUR) return "Minimum is 1 hour";
                  if (value > MAX_HOUR) return "Maximum is 24 hours";
                }
                return true; // valid
              },
            }}
            render={({ field, fieldState }) => (
              <div className="flex flex-col gap-2 w-[100px]">
                <Input
                  {...field}
                  // value={field?.value?.toString()}
                  // onChange={(e) => field.onChange(parseFloat(e.target.value))}
                  type="number"
                  placeholder="duration"
                  className="bg-gray-600 w-[100px]"
                />
                {fieldState.error && (
                  <p className="text-red-500 text-xs">
                    {fieldState.error.message}
                  </p>
                )}
              </div>
            )}
          />

          <Controller
            name="durationType"
            control={control}
            render={({ field }) => {
              const selectedValue = field?.value;
              return (
                <DropdownMenu>
                  <DropdownMenuTrigger
                    defaultValue={durationTypes[0]}
                    className="flex items-center justify-between font-montserrat text-left text-charcoal font-semibold text-xs  border rounded-md h-max py-1.5 px-2 w-30"
                  >
                    {selectedValue}
                    <ChevronDown />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="bg-white" align="start">
                    {durationTypes?.map((durationTypeOption) => {
                      return (
                        <DropdownMenuItem
                          key={durationTypeOption}
                          className="text-xs outline-none hover:bg-gray-100 font-medium hover:cursor-pointer"
                          onSelect={() => field.onChange(durationTypeOption)}
                        >
                          {durationTypeOption}
                        </DropdownMenuItem>
                      );
                    })}
                  </DropdownMenuContent>
                </DropdownMenu>
              );
            }}
          />
        </form>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button
            onClick={handleSubmit(handleSave)}
            type="submit"
            className="text-white"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomDuration;
